export interface Volume {
  number: number
  /** Spine colour on the product mockup. */
  accent: 'gold' | 'blue' | 'green' | 'red' | 'purple' | 'slate'
  mostUsed?: boolean
  name: string
  pages: number
  subtitle: string
  image: string
  alt: string
  covers: string[]
  solves: string
  result: string
}

export const volumes: Volume[] = [
  {
    number: 1,
    accent: 'gold',
    mostUsed: true,
    name: 'Sprint & Hurdles Mastery',
    pages: 50,
    subtitle: 'Complete system for 100m, 200m, 400m and hurdle events',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/e33e8223-ec33-4347-9fcc-92c4068780e5.jpg',
    alt: 'Sprinter exploding out of the starting blocks.',
    covers: [
      'Acceleration mechanics and phase development',
      '15 sprint acceleration sessions (Tier 1, 2, 3)',
      '15 maximum velocity and speed development sessions',
      '10 hurdle rhythm and clearance sessions',
      'Lead leg, trail leg, and complete hurdle mechanics',
      'Technical assessment rubrics',
      'Coaching cues for every drill',
      'Common errors and progressions to fix them',
    ],
    solves:
      'Speed training requires more than telling athletes to sprint harder. This volume gives you structured sessions for developing acceleration, mechanics, maximum velocity and speed qualities progressively—without guessing the rest times or volume.',
    result:
      'You stop searching for isolated sprint drills and start coaching speed through a clear progression from foundation to elite performance.',
  },
  {
    number: 2,
    accent: 'blue',
    name: 'The Jump Lab',
    pages: 50,
    subtitle:
      'Complete system for Long Jump, Triple Jump, High Jump and Pole Vault',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/53f8192a-2502-429c-8c15-345f5614012c.jpg',
    alt: 'Long jumper in flight above the sand pit.',
    covers: [
      'Long Jump approach consistency and takeoff progressions',
      '12 long jump sessions (from basic to advanced)',
      'Triple jump phase work and rhythm development',
      '10 triple jump sessions with safety progressions',
      'High jump curve, penultimate step, and bar clearance',
      '12 high jump sessions organized by difficulty',
      'Pole vault fundamentals and safe technical progression',
      '8 pole vault sessions with qualified safety protocols',
      'Landing mechanics and injury prevention',
      'Assessment sheets and approach consistency tracking',
    ],
    solves:
      'Jump performance depends on much more than simply taking more attempts. You receive structured progressions from approach fundamentals to competition-relevant execution. When an approach breaks down, you know exactly what to coach next.',
    result:
      'Athletes develop cleaner approaches, more reliable takeoffs, and consistent rhythm—without random attempts or guesswork.',
  },
  {
    number: 3,
    accent: 'green',
    name: 'Throws Power System',
    pages: 50,
    subtitle: 'Complete system for Shot Put, Discus, Javelin and Hammer',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/ba255161-ed21-409f-b9ee-82d451800bf9.jpg',
    alt: 'Discus thrower mid-rotation in the throwing circle.',
    covers: [
      'Shot put grip, stance, and force transfer progressions',
      '12 shot put sessions from technical foundation to competition weight',
      'Discus footwork, rotation, and release mechanics',
      '12 discus sessions with implement progressions',
      'Javelin approach, plant, block, and throw sequencing',
      '12 javelin sessions organized by technical focus',
      'Hammer swing basics and safe throwing progression',
      '8 hammer sessions with safety-first protocols',
      'Throwing area organization and safety protocols',
      'Assessment rubrics for technical execution',
    ],
    solves:
      'Throwing events involve complex sequencing and strict safety requirements. This volume gives you progressive systems for developing each event logically while maintaining safety standards.',
    result:
      'Throwing sessions become safer, more organized, and more technically purposeful. Athletes progress faster because each session builds on the previous one.',
  },
  {
    number: 4,
    accent: 'red',
    name: 'Middle Distance, Distance & Race Walking',
    pages: 30,
    subtitle: 'Complete system for 800m, 1500m, 5K, 10K and Race Walking',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/feb090b9-891b-4add-8374-9881d3d33116.jpg',
    alt: 'Pack of middle-distance runners rounding the curve.',
    covers: [
      'Aerobic base building and long run progressions',
      'Threshold development and lactate processing',
      'Race-pace repeats and competition simulation',
      '12 middle distance sessions (800m-1500m specific)',
      '12 distance sessions (5K-10K specific)',
      'Pacing strategies and tactical execution',
      '8 race walking sessions with technique progressions',
      'Running economy drills and cadence work',
      'Assessment tests (12-minute run, 2000m time trial, 20-min threshold)',
    ],
    solves:
      'Endurance coaching requires control of intensity, volume and progression. This volume gives you structured sessions instead of random mileage or repeated hard intervals.',
    result:
      'You can develop endurance athletes with a clearer purpose behind every session. No more guessing on pace or duration.',
  },
  {
    number: 5,
    accent: 'purple',
    name: 'Complete Track & Field Coaching Vault',
    pages: 30,
    subtitle:
      'Complete system for organizing, managing and executing your entire program',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/99b13a12-505c-4f9e-819b-7dba178eccb1.jpg',
    alt: 'Coach directing a large group rotating through training stations.',
    covers: [
      'Managing mixed-event groups efficiently',
      'Station rotation systems for large teams',
      'Preseason structure and assessment protocols',
      'In-season maintenance and readiness management',
      'Competition week organization and tapering',
      'Athlete readiness monitoring without complex software',
      'Testing protocols and monthly progress tracking',
      'Combined events programming',
      'One-coach team management strategies',
      'Large group organization systems',
      'Time-efficient practice design',
    ],
    solves:
      'Track & Field coaches rarely work with only one athlete and one event. This volume helps you manage the entire coaching environment—from arrival to competition execution.',
    result:
      'You move from running individual sessions to managing a complete Track & Field program with confidence and control.',
  },
  {
    number: 6,
    accent: 'slate',
    name: 'Periodization Blueprint',
    pages: 30,
    subtitle: 'Complete 52-week annual training strategy',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/c07b32f7-2c9a-482e-9c98-932eab33b4af.jpg',
    alt: 'Annual training calendar, stopwatch and whistle on a coach’s desk.',
    covers: [
      'The 4-macrocycle annual plan explained',
      'Block vs. Linear periodization comparison',
      'Event-specific periodization models (sprints, distance, jumps, throws)',
      'Preseason assessment and planning',
      'Macrocycle 1: Anatomical Adaptation & Base Building (Weeks 1-8)',
      'Macrocycle 2: Strength & Power Development (Weeks 9-14)',
      'Macrocycle 3: Competition Preparation & Peak Building (Weeks 15-20)',
      'Macrocycle 4: Competition & Maintenance (Weeks 21-52)',
      'Multiple mini-peaks strategy',
      'Adjusting based on performance data',
      'Athlete variability and individual periodization',
      '52-week master calendar template',
      'Weekly planning and microcycle design',
    ],
    solves:
      'Most coaches plan week-to-week. Elite coaches plan for the entire year. This volume gives you the strategic framework to build a championship season.',
    result:
      'You stop guessing when to build strength, when to sharpen speed, and how to taper for peak performance. Every training block has purpose and timing.',
  },
]
